import { listConversations } from '../services/Botpress';
import { useNavigate } from 'react-router-dom';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { styled } from '@stitches/react';
import { each, filter, first, last, sortBy } from 'underscore';
import InputBar from './InputBar';
import { HamburgerMenuIcon, ExitIcon } from "@radix-ui/react-icons";
import { useChat } from '../context/ChatContext';
import { listMessages } from '../services/Botpress';
import { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { getUser } from '../services/Botpress';
import { logout } from '../services/Auth';

//#region styles
const SidebarContainer = styled('div', {
  transition: 'width 0.3s ease-in-out',
  borderRight: '1px solid #2d3135',
  backgroundColor: '#1C2024',
  height: '100vh',
  position: 'relative',
});

const ChatList = styled(ScrollArea.Root, {
  height: 'auto',
});

const ChatViewport = styled(ScrollArea.Viewport, {
  height: 'auto',
});

const ChatItem = styled('div', {
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#252a2e',
  },
});

const ChatInfo = styled('div', {
  flex: 1,
});

const ChatName = styled('div', {
  fontWeight: 'bold',
  color: '#ffffff',
});

const LastMessage = styled('div', {
  color: '#9ba4ab',
  fontSize: '14px',
});

const ToggleButton = styled('button', {
  position: 'relative',
  margin: 0,
  background: '#5b5bd6',
  border: 'none',
  color: '#ffffff',
  fontSize: '24px',
  cursor: 'pointer',
  transition: 'left 0.3s ease-in-out',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px '
});

const CloseButton = styled('button', {
  position: 'relative',
  margin: '2.5%',
  width: '100%',
  background: '#5b5bd6',
  border: 'none',
  color: '#ffffff',
  fontSize: '15px',
  cursor: 'pointer',
  transition: 'left 0.3s ease-in-out',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px '
});
//#endregion

const ChatSidebar = ({ onChatSelect, selectedChat }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { getMessages, setMessages } = useChat();
  const { getConversations, setConversations } = useChat();
  const fetchedChats = useRef(new Set());
  const [loading, setLoading] = useState(true); // Estado para controlar la carga
  const navigate = useNavigate();

  // Función para obtener el texto del último mensaje
  const getLastMessageText = (conversationId, property) => {
    const messages = filter(getMessages(conversationId), (message) => message.show !== false);
    if (!messages || messages.length === 0) {
      return 'Sin mensajes';
    }

    const lastMessage = last(messages);

    if (property === "text") {
      if (lastMessage.type === "attachment")
        return lastMessage.payload?.text || lastMessage.content;

      if (lastMessage.type === "audio")
        return "Audio";

      if (lastMessage.type === "image")
        return "Imagen";

      if (lastMessage.type === "file")
        return "Archivo";

      if (lastMessage.type === "video")
        return "Video";

      return lastMessage.payload.text.length > 45 ? `${lastMessage.payload.text.substring(0, 45)}...` : lastMessage.payload.text;
    }

    if (property === "name")
      return lastMessage.userName ?? lastMessage?.tags[property];

    return lastMessage.tags[property];
  };

  // Cargar conversaciones iniciales
  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true);
      try {
        const response = await listConversations();
        const sortedConversations = sortBy(response.data.conversations, 'updatedAt').reverse();

        await Promise.all(
          sortedConversations.map(async (chat) => {
            if (!chat?.id || fetchedChats.current.has(chat.id)) return;
            try {
              const response = await listMessages(chat.id),
                newMessages = sortBy(response.data.messages, 'createdAt'),
                userId = first(filter(newMessages, { direction: "incoming" })).userId,
                userIncomingName = await getUser(userId);

              each(newMessages, (message) => message.tags = userIncomingName.data.user.tags);
              setMessages(chat.id, newMessages, {});
              fetchedChats.current.add(chat.id);
            } catch (error) {
              console.error('Error al obtener los mensajes:', error);
            }
          })
        );
        setConversations(sortedConversations);
      } catch (error) {
        console.error('Error al obtener las conversaciones:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [setConversations, setMessages]);

  // Renderizar skeleton mientras se cargan las conversaciones
  const renderSkeletons = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <ChatItem key={index}>
        <ChatInfo>
          <Skeleton width={150} height={20} />
          <Skeleton width={200} height={15} style={{ marginTop: '5px' }} />
        </ChatInfo>
      </ChatItem>
    ));
  };

  // Manejar el cierre de sesión
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <SidebarContainer style={{ width: isSidebarOpen ? '350px' : '50px' }}>
      <div style={{ width: '100', padding: '10px 10px 0 10px', display: 'flex' }}>
        <ToggleButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
          <HamburgerMenuIcon width={20} height={20} />
        </ToggleButton>
      </div>
      <div style={{ display: isSidebarOpen ? 'block' : 'none' }}>
        <InputBar placeholder={'Buscar chat'} />
      </div>

      <ChatList>
        <ChatViewport style={{ width: isSidebarOpen ? 'auto' : '0px', overflowY: 'scroll', height: 'calc(100vh - 20vh)' }}>
          {loading ? (
            renderSkeletons() // Renderiza los skeletons mientras se cargan las conversaciones
          ) : getConversations() && getConversations().length > 0 ? (
            getConversations().map((chat) => (
              <ChatItem
                key={chat.id}
                onClick={() => {
                  onChatSelect(chat);
                  navigate('/chat');
                }}
                style={{
                  backgroundColor: selectedChat?.id === chat.id ? '#5b5bd6' : 'transparent',
                }}
              >
                <ChatInfo>
                  <ChatName>{getLastMessageText(chat.id, "name")}</ChatName>
                  <LastMessage>{getLastMessageText(chat.id, "text")}</LastMessage>
                </ChatInfo>
              </ChatItem>
            ))
          ) : (
            <div>No hay conversaciones</div>
          )}
        </ChatViewport>
        <ScrollArea.Scrollbar orientation="vertical">
          <ScrollArea.Thumb />
        </ScrollArea.Scrollbar>
      </ChatList>

      <div style={{ width: '100%', display: 'flex', position: 'absolute', bottom: '2%' }}>
        <CloseButton style={{ margin: isSidebarOpen ? '2.5%' : '20%' }} onClick={handleLogout}>{isSidebarOpen ? "Cerrar Sesión" : <ExitIcon width={20} height={20} />}</CloseButton>
      </div>
    </SidebarContainer>
  );
};

export default ChatSidebar;
